import { APP_DATE_FORMAT_EXPANDED } from "@/config/constants";
import { Button, Card, List, ListItem, ListItemIcon, ListItemText } from "@/components/ui";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { IEdoQuestionAnswer } from "@/shared/model/edo-question-answer.model";
import { IEdoQuizAttempt } from "@/shared/model/edo-quiz-attempt.model";
import { IEdoQuizQuestion } from "@/shared/model/edo-quiz-question.model";
import { getEntity } from "@/shared/reducers/entities/edo-quiz-attempt.reducer";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { CheckSquare, Loader2, Square } from "lucide-react";
import axios, { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { TextFormat, Translate } from "react-jhipster";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import QuestionAnswer from "./component/QuestionAnswer";

const QuizAttemptReviewRoute = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const { updating } = useAppSelector<EntityState<IEdoQuestionAnswer>>(
    (state) => state.edoQuestionAnswer
  );
  const { entity: quizAttempt } = useAppSelector<EntityState<IEdoQuizAttempt>>(
    (state) => state.edoQuizAttempt
  );
  const [questions, setQuestions] = useState<
    { quizQuestion: IEdoQuizQuestion; questionAnswer?: IEdoQuestionAnswer }[]
  >([]);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [score, setScore] = useState<{
    score: number;
    totalScore: number;
  }>({ score: 0, totalScore: 0 });
  const [questionAnswer, setQuestionAnswer] = useState<{
    quizQuestion: IEdoQuizQuestion;
    questionAnswer: IEdoQuestionAnswer;
  }>();

  const getQuizQuestions = async () => {
    const response = await axios.get<{ data: IEdoQuizQuestion[] }>(
      `/quiz-attempts/${params.id}/questions`,
      { params: { sort: "updatedAt,ASC" } }
    );
    setQuestions(response.data.data.map((quizQuestion) => ({ quizQuestion })));
  };

  const getQuizAttemptScore = async () => {
    const response = await axios.get<{
      data: { score: number; totalScore: number };
    }>(`/quiz-attempts/${params.id}/score`);
    setScore(response.data.data);
  };

  useEffect(() => {
    if (params.id) {
      dispatch(getEntity(params.id));
      getQuizQuestions();
      getQuizAttemptScore();
    }
  }, [params.id]);

  const handleTerminateReview = async () => {
    try {
      await axios.patch(`/quiz-attempts/${params.id}/review`);
      toast.success("Revisione terminata con successo.");
      navigate(
        `/courses/${quizAttempt?.courseQuiz?.course?.id}/quizzes/${quizAttempt?.courseQuiz?.id}/results`
      );
    } catch (e) {
      if (e instanceof AxiosError) {
        toast.error(e.response?.data?.message);
        return;
      }
      toast.error("Errore durante la terminazione della revisione.");
    }
  };

  useEffect(() => {
    if (quizAttempt?.timeFinish) {
      const timeStart = new Date(quizAttempt.timeStart).getTime();
      const timeFinish = new Date(quizAttempt.timeFinish).getTime();
      setTimeLeft((timeFinish - timeStart) / 1000);
    }
  }, [quizAttempt?.id]);

  useEffect(() => {
    const questionAnswerIndex = questions.findIndex(
      (q) => q?.quizQuestion?.id === questionAnswer?.quizQuestion?.id
    );
    if (questionAnswerIndex !== -1) {
      const newQuestions = [...questions];
      newQuestions[questionAnswerIndex].questionAnswer =
        questionAnswer!.questionAnswer;
      setQuestions(newQuestions);
      getQuizAttemptScore();
    }
  }, [questionAnswer]);

  const handleUpdateAnswer = (
    quizQuestion: IEdoQuizQuestion,
    questionAnswerData: IEdoQuestionAnswer
  ) => {
    setQuestions((prevQuestions) => {
      const idx = prevQuestions.findIndex(
        (q) => q?.quizQuestion?.id === quizQuestion?.id
      );
      if (idx === -1) return prevQuestions;
      const next = [...prevQuestions];
      next[idx].questionAnswer = questionAnswerData;
      return next;
    });
    getQuizAttemptScore();
  };

  const reviewedCount = questions.reduce(
    (acc, cur) =>
      cur.questionAnswer?.lastReview != null ? acc + 1 : acc,
    0
  );
  const scoredCount = questions.reduce(
    (acc, cur) =>
      cur.questionAnswer?.score != null ? acc + 1 : acc,
    0
  );
  const allScored = scoredCount === questions.length && questions.length > 0;

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <div className="lg:col-span-2 space-y-5">
        <Card className="surface overflow-hidden rounded-3xl p-0">
          <div className="p-6">
            <h1 className="text-heading font-semibold text-[var(--color-mockup-text)]">
              {quizAttempt?.courseQuiz?.quiz?.title}
            </h1>
            <p className="mt-2 text-body text-[var(--color-mockup-text-mid)]">
              {quizAttempt?.courseQuiz?.quiz?.description}
            </p>
          </div>
        </Card>
        <div className="space-y-5">
          {questions.length ? (
            questions.map((question) => (
              <QuestionAnswer
                key={question.quizQuestion?.id}
                quizQuestion={question.quizQuestion}
                quizAttempt={quizAttempt!}
                onQuestionAnswer={(qa) =>
                  handleUpdateAnswer(question.quizQuestion, qa)
                }
              />
            ))
          ) : (
            <Card className="surface p-6 text-center">
              <p className="text-body text-[var(--color-mockup-text-mid)]">
                <Translate contentKey="edocendoApp.edoQuestion.home.notFound" />
              </p>
            </Card>
          )}
        </div>
      </div>

      <div className="space-y-5">
        <Card className="surface overflow-hidden rounded-3xl p-0">
          <div className="border-b border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/50 px-4 py-3 dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]/30">
            <h2 className="text-body font-semibold text-[var(--color-mockup-text)]">
              Riepilogo
            </h2>
          </div>
          <div className="p-4 space-y-4">
            <div>
              <p className="text-body font-medium text-[var(--color-mockup-text)]">
                Consegnato il
              </p>
              <p className="text-body-sm text-[var(--color-mockup-text-mid)]">
                <TextFormat
                  value={quizAttempt?.timeFinish}
                  type="date"
                  format={APP_DATE_FORMAT_EXPANDED}
                />
              </p>
            </div>
            <div>
              <p className="text-body font-medium text-[var(--color-mockup-text)]">
                Durata tentativo
              </p>
              <p className="text-body-sm text-[var(--color-mockup-text-mid)]">
                {`${Math.trunc(timeLeft / 60 / 60)} ore, ${Math.trunc((timeLeft / 60) % 60)} minuti, ${Math.trunc(timeLeft % 60)} secondi`}
              </p>
            </div>
            <div>
              <p className="text-body font-medium text-[var(--color-mockup-text)]">
                Punteggio
              </p>
              <p className="text-body-sm text-[var(--color-mockup-text-mid)]">
                {`${score.score} / ${score.totalScore} ${score.totalScore === 1 ? "punto" : "punti"}`}
              </p>
            </div>
            <div>
              <p className="text-body font-medium text-[var(--color-mockup-text)]">
                Domande revisionate
              </p>
              <p className="text-body-sm text-[var(--color-mockup-text-mid)]">
                {`${reviewedCount} / ${questions.length} ${questions.length === 1 ? "domanda" : "domande"}`}
              </p>
            </div>
            <Button
              onClick={handleTerminateReview}
              disabled={!allScored}
              className="w-full"
            >
              {updating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Termina revisione"
              )}
            </Button>
            <div className="flex justify-center pt-2">
              <Link
                to={`/courses/${quizAttempt?.courseQuiz?.course?.id}/quizzes/${quizAttempt?.courseQuiz?.id}/results`}
                className="text-body-sm font-medium text-primary hover:underline"
              >
                Torna ai risultati
              </Link>
            </div>
          </div>
        </Card>
        <Card className="surface overflow-hidden rounded-3xl p-0">
          <div className="border-b border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/50 px-4 py-3 dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]/30">
            <h2 className="text-body font-semibold text-[var(--color-mockup-text)]">
              Domande
            </h2>
          </div>
          <List className="p-0">
            {questions?.length ? (
              questions.map((question, index) => (
                <ListItem
                  key={question.quizQuestion?.id}
                  className="flex items-center gap-3 py-3"
                >
                  <ListItemIcon>
                    {!question.questionAnswer ||
                    question.questionAnswer?.lastReview == null ? (
                      <Square className="h-5 w-5 text-[var(--color-mockup-text-light)]" />
                    ) : (
                      <CheckSquare className="h-5 w-5 text-primary" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={`Domanda ${index + 1}`}
                  />
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText
                  primary={
                    <span className="text-body text-[var(--color-mockup-text-mid)]">
                      <Translate contentKey="edocendoApp.edoCourseQuestion.home.notFound" />
                    </span>
                  }
                />
              </ListItem>
            )}
          </List>
        </Card>
      </div>
    </div>
  );
};

export default QuizAttemptReviewRoute;
